/**
 * Companion variable type options
 */
export type CompanionVariableType = "Order" | "Label" | "Unit";

/**
 * Configuration mapping for companion variable display formatting.
 * Defines how each companion variable type is formatted when displayed.
 */
export const COMPANION_TYPE_CONFIG: Record<CompanionVariableType, (variable: string) => string> = {
  Order: (variable) => `/${variable}`,
  Label: (variable) => `/${variable}`,
  Unit: (variable) => ` (${variable})`,
};

/**
 * Represents a single companion variable with its type and value.
 *
 * @interface CompanionVariable
 * @property variable - Full variable identifier in format "dataset.variable".
 * @property type - Type of companion variable ("Order" | "Label" | "Unit").
 */
export interface CompanionVariable {
  variable: string;
  type: CompanionVariableType;
}

/**
 * Data transfer object for clinical variables with companion variables.
 *
 * @interface VariableDto
 * @property nameVariable - Internal name of the primary variable.
 * @property dataset - Name of the dataset containing the primary variable.
 * @property companionVariables - Array of companion variables. Order in array determines display order.
 */
export interface VariableDto {
  nameVariable: string;
  dataset: string;
  companionVariables?: CompanionVariable[];
}

/**
 * Type guard to check if an object conforms to VariableDto.
 *
 * @param obj - Value to test
 * @returns True if obj matches VariableDto
 */
export const isVariableDto = (obj: unknown): obj is VariableDto => {
  // Check object
  if (typeof obj !== "object" || obj === null) return false;

  // Check basic props
  const chkObj = obj as VariableDto;
  if (typeof chkObj.nameVariable !== "string") return false;
  if (typeof chkObj.dataset !== "string") return false;

  // Check optional companionVariables array
  if (chkObj.companionVariables !== undefined) {
    if (!Array.isArray(chkObj.companionVariables)) return false;
    for (const companion of chkObj.companionVariables) {
      if (typeof companion !== "object" || companion === null) return false;
      if (typeof companion.variable !== "string") return false;
      if (!["Order", "Label", "Unit"].includes(companion.type)) return false;
    }
  }

  // Everything is good
  return true;
};
